/**
 * PhoneField — shared phone-number input with country-code flag picker.
 *
 * Storage contract (same as backend normalizePhone):
 *   digits only, WITH country code, NO leading +
 *   e.g.  "917536864032"
 *
 * The component accepts/emits this digits-only string so callers
 * can store it as-is and pass it straight to the WhatsApp API.
 *
 * For display, prepend "+" wherever you render the stored value.
 */
import 'react-phone-input-2/lib/material.css';
import PhoneInput from 'react-phone-input-2';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Box, FormHelperText, Typography } from '@mui/material';

interface PhoneFieldProps {
  /** Current value — digits-only with country code, e.g. "917536864032" */
  value: string;
  /** Called with the new digits-only value (never has a leading +) */
  onChange: (digits: string) => void;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /** Default country shown when field is empty. Defaults to 'in' (India). */
  defaultCountry?: string;
  placeholder?: string;
  /** Compact styling for embedding inside MUI dialogs */
  size?: 'normal' | 'small';
}

export default function PhoneField({
  value,
  onChange,
  label,
  required,
  error,
  disabled,
  defaultCountry = 'in',
  placeholder,
  size = 'normal',
}: PhoneFieldProps) {
  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: size === 'small' ? 36 : 40,
    fontSize: size === 'small' ? '0.875rem' : '0.9375rem',
    borderRadius: 8,
    borderColor: error ? '#ef4444' : '#e2e8f0',
    backgroundColor: '#fafbfc',
    color: '#0f172a',
    paddingTop: 0,
    paddingBottom: 0,
  };

  const buttonStyle: React.CSSProperties = {
    borderRadius: '8px 0 0 8px',
    borderColor: error ? '#ef4444' : '#e2e8f0',
    backgroundColor: '#f8fafc',
  };

  return (
    <Box>
      {label && (
        <Typography
          component="label"
          sx={{
            display: 'block',
            mb: 0.75,
            fontSize: size === 'small' ? '0.8125rem' : '0.875rem',
            fontWeight: 600,
            color: error ? '#ef4444' : '#334155',
          }}
        >
          {label}
          {required && (
            <Typography component="span" color="error" ml={0.5}>
              *
            </Typography>
          )}
        </Typography>
      )}

      <PhoneInput
        country={defaultCountry}
        value={value}
        onChange={(phone) => onChange(phone)} // phone is already digits-only from the library
        {...(disabled !== undefined ? { disabled } : {})}
        {...(placeholder ? { placeholder } : {})}
        inputStyle={inputStyle}
        buttonStyle={buttonStyle}
        containerStyle={{ width: '100%' }}
        // Let the user type any international number
        enableSearch
        searchPlaceholder="Search country..."
        // Keep digits only (library default) — no + prefix stored
        disableCountryCode={false}
        // The material skin's own floating label defaults to "Phone" and
        // overlaps the label we already render above — suppress it.
        specialLabel=""
      />

      {error && (
        <FormHelperText error sx={{ mt: 0.5, ml: 1.75 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
}

/**
 * Helper: add + prefix for display only.
 * Use the raw digits string everywhere else.
 */
export function formatPhoneDisplay(digits: string): string {
  if (!digits) return '';
  return `+${digits}`;
}

/**
 * Client-side validation — mirrors backend isValidPhone. A digit-count
 * check alone can't tell a number missing its country code from a real
 * one of the same length, so this defers to libphonenumber-js's
 * per-country number-plan rules instead of a heuristic.
 */
export function isValidPhone(digits: string): boolean {
  if (!digits) return false;
  if (!/^\d+$/.test(digits)) return false;
  if (digits[0] === '0') return false;
  const parsed = parsePhoneNumberFromString(`+${digits}`);
  return Boolean(parsed?.isValid());
}
